import { useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { ArrowLeft, ArrowRight, Logo, Check, Shield, Sparkles } from '../components/Icons.jsx';
import { Intro, Done, mainStyle, difficultyPill } from './VennArchitect.jsx';
import { TutorNudge } from '../components/TutorCard.jsx';
import { tutorFor } from '../lib/tutors.js';
import DrillResultBar from '../components/DrillResultBar.jsx';

// 5 axes × 3 questions = 15. Each scenario asks appropriateness 1-4.
// Axis: integrity, teamwork, communication, safety, professionalism.
const scenarios = [
  // Integrity
  { axis: 'integrity', text: 'You are a medical student on placement. A consultant signs your reflection logbook without reading it. You need it signed to pass the year. What is the most appropriate action?', options: [
    { text: 'Accept the signature and move on', a: 4 },
    { text: 'Politely ask the consultant to read it briefly before signing', a: 1 },
    { text: 'Forge a second signature elsewhere to be safe', a: 4 },
    { text: 'Hand it to a different consultant unread and ask them to sign too', a: 4 }
  ]},
  { axis: 'integrity', text: 'You see a fellow student copying answers in an end-of-rotation written test.', options: [
    { text: 'Report it to the invigilator at the end of the test', a: 1 },
    { text: 'Confront them after the test and tell them to self-report', a: 2 },
    { text: 'Ignore it — it doesn\'t affect your mark', a: 4 },
    { text: 'Tell other students about it as a warning', a: 4 }
  ]},
  { axis: 'integrity', text: 'A friend asks you to write a fake reference for their volunteering. They did some of it.', options: [
    { text: 'Refuse and explain why', a: 1 },
    { text: 'Write it but only about the bits they did do', a: 3 },
    { text: 'Write what they ask — it\'s only partly fake', a: 4 },
    { text: 'Refer them to the actual supervisor who can write a real one', a: 1 }
  ]},
  // Teamwork
  { axis: 'teamwork', text: 'A nurse asks you to administer a medication. You\'re a medical student and not authorised.', options: [
    { text: 'Decline and explain you\'re a student, find a junior doctor', a: 1 },
    { text: 'Administer it — the nurse said it was fine', a: 4 },
    { text: 'Refuse and walk away without saying why', a: 3 },
    { text: 'Tell the patient you can\'t help and leave', a: 3 }
  ]},
  { axis: 'teamwork', text: 'During a team meeting, the lead consultant proposes a plan you think is wrong.', options: [
    { text: 'Raise the concern respectfully in the meeting', a: 1 },
    { text: 'Stay quiet — it\'s not your place as a student', a: 3 },
    { text: 'Approach the consultant privately afterwards', a: 2 },
    { text: 'Email the patient\'s family with your concern', a: 4 }
  ]},
  { axis: 'teamwork', text: 'You overhear two nurses gossiping about a patient at the nurse\'s station.', options: [
    { text: 'Move out of earshot and continue your work', a: 3 },
    { text: 'Politely remind them of confidentiality', a: 1 },
    { text: 'Report them to the ward sister', a: 2 },
    { text: 'Join the conversation', a: 4 }
  ]},
  // Communication
  { axis: 'communication', text: 'A patient with limited English seems confused about their discharge instructions.', options: [
    { text: 'Speak slowly and check understanding with teach-back', a: 1 },
    { text: 'Hand them a written leaflet only', a: 3 },
    { text: 'Ask for a professional interpreter', a: 1 },
    { text: 'Discharge them — they nodded so they understand', a: 4 }
  ]},
  { axis: 'communication', text: 'A patient\'s relative is angry about a long wait time.', options: [
    { text: 'Listen, acknowledge the frustration and explain delays', a: 1 },
    { text: 'Tell them it\'s the NHS and waits are normal', a: 4 },
    { text: 'Walk away to avoid escalation', a: 4 },
    { text: 'Call security', a: 3 }
  ]},
  { axis: 'communication', text: 'A consultant asks you to break difficult news to a patient on your own.', options: [
    { text: 'Decline and ask for the consultant to lead, you observe', a: 1 },
    { text: 'Do it — they\'ll see your initiative', a: 4 },
    { text: 'Ask another junior doctor to come with you', a: 3 },
    { text: 'Refuse outright with no explanation', a: 3 }
  ]},
  // Safety
  { axis: 'safety', text: 'You realise you may have given a wrong dose. The patient seems fine.', options: [
    { text: 'Inform the supervising doctor immediately and complete an incident report', a: 1 },
    { text: 'Monitor the patient and only act if symptoms appear', a: 4 },
    { text: 'Tell the next shift to keep an eye on them', a: 3 },
    { text: 'Do nothing — the patient is fine', a: 4 }
  ]},
  { axis: 'safety', text: 'You see a consultant about to perform a procedure without washing hands.', options: [
    { text: 'Politely remind them about hand hygiene', a: 1 },
    { text: 'Say nothing — they\'re senior to you', a: 4 },
    { text: 'Report them to infection control afterwards', a: 3 },
    { text: 'Refuse to assist with the procedure', a: 3 }
  ]},
  { axis: 'safety', text: 'A junior doctor on your team appears unsteady on their feet and smells of alcohol.', options: [
    { text: 'Speak to them privately and escalate to a senior immediately', a: 1 },
    { text: 'Cover their shift quietly', a: 4 },
    { text: 'Wait and see if they recover during the day', a: 4 },
    { text: 'Tell the patient nearby to be careful', a: 4 }
  ]},
  // Professionalism
  { axis: 'professionalism', text: 'A patient adds you on social media after a hospital stay.', options: [
    { text: 'Decline the request', a: 1 },
    { text: 'Accept — they were friendly', a: 4 },
    { text: 'Ignore the request and don\'t engage', a: 2 },
    { text: 'Accept but message asking them not to discuss medical matters', a: 3 }
  ]},
  { axis: 'professionalism', text: 'You post on social media frustrated about a long shift, mentioning the ward name.', options: [
    { text: 'Delete the post immediately', a: 1 },
    { text: 'Edit out the ward name and leave the rest', a: 3 },
    { text: 'Leave it — it\'s your personal account', a: 4 },
    { text: 'Set the account to private then leave it', a: 3 }
  ]},
  { axis: 'professionalism', text: 'A patient offers you a £20 note as thanks for your care.', options: [
    { text: 'Politely decline and explain you can\'t accept', a: 1 },
    { text: 'Accept and donate it to charity', a: 3 },
    { text: 'Accept the gift gratefully', a: 4 },
    { text: 'Decline and suggest a small thank-you card instead', a: 1 }
  ]}
];

const axes = {
  integrity: { label: 'Integrity', emoji: '⚖️', desc: 'Honesty, owning mistakes, doing the right thing under pressure.' },
  teamwork: { label: 'Teamwork', emoji: '🤝', desc: 'Knowing your role, deferring appropriately, supporting colleagues.' },
  communication: { label: 'Communication', emoji: '💬', desc: 'Listening, clarity, adapting to the patient or relative in front of you.' },
  safety: { label: 'Patient safety', emoji: '🛡️', desc: 'Escalating concerns, hygiene, incident reporting, harm avoidance.' },
  professionalism: { label: 'Professionalism', emoji: '🎓', desc: 'Boundaries, GMC standards, public conduct, gifts and social media.' }
};

const ratings = [
  { v: 1, label: 'Very appropriate' },
  { v: 2, label: 'Appropriate, not ideal' },
  { v: 3, label: 'Inappropriate, not awful' },
  { v: 4, label: 'Very inappropriate' }
];

export default function DecisionProfiler() {
  const [stage, setStage] = useState('intro');
  const [length, setLength] = useState(15);
  const [idx, setIdx] = useState(0);
  const [answers, setAnswers] = useState([]);
  const [picked, setPicked] = useState(null);
  const [reviewIdx, setReviewIdx] = useState(0);

  const questions = useMemo(() => {
    const shuffled = [...scenarios].sort(() => Math.random() - 0.5);
    return shuffled.slice(0, Math.min(length, scenarios.length));
  }, [length, stage]);

  const flat = useMemo(() => questions.flatMap((s) => s.options.map((o, i) => ({ ...o, parentIdx: questions.indexOf(s), optionIdx: i, axis: s.axis, scenarioText: s.text }))), [questions]);
  const q = flat[idx];

  const start = () => { setIdx(0); setAnswers([]); setPicked(null); setReviewIdx(0); setStage('drill'); };
  const next = () => {
    const a = { ...q, given: picked, delta: Math.abs(picked - q.a) };
    const newAnswers = [...answers, a];
    setAnswers(newAnswers);
    setPicked(null);
    if (idx < flat.length - 1) setIdx(idx + 1);
    else setStage('done');
  };

  const axisScores = useMemo(() => {
    const out = {};
    Object.keys(axes).forEach((k) => { out[k] = { total: 0, max: 0 }; });
    answers.forEach((a) => {
      const score = Math.max(0, 3 - a.delta);
      out[a.axis].total += score;
      out[a.axis].max += 3;
    });
    return out;
  }, [answers]);

  const overallPct = useMemo(() => {
    const total = Object.values(axisScores).reduce((s, a) => s + a.total, 0);
    const max = Object.values(axisScores).reduce((s, a) => s + a.max, 0);
    return max > 0 ? Math.round((total / max) * 100) : 0;
  }, [axisScores]);

  return (
    <div style={{ minHeight: '100vh', background: 'var(--bg)' }}>
      <header style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '12px 18px', background: '#fff', borderBottom: '1px solid var(--border)' }}>
        <Link to="/practice" style={{ display: 'inline-flex', alignItems: 'center', gap: 6, color: 'var(--text-2)', textDecoration: 'none', fontSize: 14, fontWeight: 600 }}>
          <ArrowLeft width="16" height="16" />Exit
        </Link>
        <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
          <Logo width="26" height="26" onLight />
          <strong style={{ fontSize: 14, color: 'var(--brand-deep)', fontFamily: 'Montserrat, sans-serif', letterSpacing: '0.04em' }}>Decision profiler</strong>
        </div>
        <div style={{ width: 60 }} />
      </header>

      {stage === 'intro' && (
        <Intro
          subtest="SJT" title="Decision profiler"
          body="UCAT-style SJT diagnostic. Maps your decision-making across five axes admissions tutors care about — and shows exactly where marks leak."
          how={[
            'Rate each option as Very appropriate → Very inappropriate',
            'Partial credit: an answer one band off still scores 2/3',
            'End: bar chart per axis showing your strengths and gaps'
          ]}
          difficulty={length === 15 ? 'full' : 'short'} setDifficulty={(v) => setLength(v === 'full' ? 15 : 8)}
          difficultyOptions={[
            { v: 'short', label: '8 questions', sub: '~3 minutes · quick read' },
            { v: 'full', label: '15 questions', sub: 'Full diagnostic across all 5 axes' }
          ]}
          ucatsen={false} setUcatsen={() => {}}
          onStart={start}
        />
      )}

      {stage === 'drill' && q && (
        <main style={mainStyle}>
          <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 13, color: 'var(--text-muted)', marginBottom: 8 }}>
            <span style={{ fontWeight: 600 }}>Option {idx + 1} of {flat.length}</span>
            <span style={{ display: 'flex', alignItems: 'center', gap: 6 }}><span>{axes[q.axis].emoji}</span>{axes[q.axis].label}</span>
          </div>
          <div style={{ height: 6, background: 'var(--card-soft)', borderRadius: 99, marginBottom: 20 }}>
            <div style={{ width: `${((idx + 1) / flat.length) * 100}%`, height: '100%', background: 'linear-gradient(90deg, var(--brand-purple), var(--brand-teal))', borderRadius: 99 }} />
          </div>

          <div className="card" style={{ marginBottom: 18, background: 'var(--card-soft)' }}>
            <div style={{ fontSize: 12, fontWeight: 700, color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: '0.06em', marginBottom: 8 }}>Scenario</div>
            <p style={{ margin: 0, fontSize: 15, lineHeight: 1.55, color: 'var(--text)' }}>{q.scenarioText}</p>
          </div>

          <div className="card" style={{ marginBottom: 18 }}>
            <div style={{ fontSize: 12, fontWeight: 700, color: 'var(--accent)', textTransform: 'uppercase', letterSpacing: '0.06em', marginBottom: 8 }}>Rate this option</div>
            <p style={{ margin: '0 0 16px', fontSize: 16, fontWeight: 600 }}>{q.text}</p>

            <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
              {ratings.map((r) => (
                <button key={r.v} onClick={() => setPicked(r.v)} style={{
                  padding: '12px 16px', textAlign: 'left', cursor: 'pointer', fontSize: 14.5, fontWeight: 600,
                  border: `2px solid ${picked === r.v ? 'var(--brand-purple)' : 'var(--border)'}`,
                  background: picked === r.v ? 'var(--accent-soft)' : '#fff',
                  color: 'var(--text)', borderRadius: 10,
                  display: 'flex', alignItems: 'center', gap: 12
                }}>
                  <span style={{ width: 24, height: 24, borderRadius: '50%', background: picked === r.v ? 'var(--brand-purple)' : 'var(--card-soft)', color: picked === r.v ? '#fff' : 'var(--text-2)', display: 'grid', placeItems: 'center', fontSize: 13, fontWeight: 700, flexShrink: 0 }}>{r.v}</span>
                  {r.label}
                </button>
              ))}
            </div>
          </div>

          <button onClick={next} disabled={picked === null} className="btn btn-primary" style={{ opacity: picked === null ? 0.5 : 1 }}>
            {idx === flat.length - 1 ? 'See results' : 'Next'} <ArrowRight width="14" height="14" />
          </button>
        </main>
      )}

      {stage === 'done' && (
        <main style={mainStyle}>
          <div style={{ fontSize: 12, color: 'var(--accent)', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.1em' }}>SJT · complete</div>
          <h1 style={{ margin: '6px 0 6px', fontSize: 'clamp(36px, 6vw, 48px)' }}>{overallPct}%</h1>
          <p style={{ color: 'var(--text-muted)', margin: '0 0 22px' }}>Partial-credit score across {flat.length} options.</p>

          <div className="card" style={{ marginBottom: 18 }}>
            <h3 style={{ margin: '0 0 14px', fontSize: 16 }}>Profile by axis</h3>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
              {Object.entries(axes).map(([k, a]) => {
                const s = axisScores[k];
                const pct = s.max > 0 ? (s.total / s.max) * 100 : 0;
                return (
                  <div key={k}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 14, marginBottom: 4 }}>
                      <span style={{ fontWeight: 600 }}>{a.emoji} {a.label}</span>
                      <span style={{ color: 'var(--text-muted)' }}>{s.total} / {s.max}</span>
                    </div>
                    <div style={{ height: 10, background: 'var(--card-soft)', borderRadius: 99, overflow: 'hidden' }}>
                      <div style={{ width: `${pct}%`, height: '100%', background: 'linear-gradient(90deg, var(--brand-purple), var(--brand-teal))', transition: 'width 500ms ease' }} />
                    </div>
                    <p style={{ margin: '4px 0 0', fontSize: 12.5, color: 'var(--text-muted)' }}>{a.desc}</p>
                  </div>
                );
              })}
            </div>
          </div>

          <h3 style={{ margin: '20px 0 10px', fontSize: 16 }}>Per-question review</h3>
          <div className="card" style={{ marginBottom: 18 }}>
            <div style={{ fontSize: 12, color: 'var(--text-muted)', marginBottom: 10 }}>Option {reviewIdx + 1} of {answers.length}</div>
            <p style={{ margin: '0 0 6px', fontSize: 14, color: 'var(--text-2)' }}>{answers[reviewIdx]?.scenarioText}</p>
            <p style={{ margin: '6px 0 10px', fontSize: 15, fontWeight: 600 }}>"{answers[reviewIdx]?.text}"</p>
            <div style={{ display: 'flex', gap: 8, fontSize: 13, marginBottom: 10 }}>
              <span style={pill('your')}>Your rating: {answers[reviewIdx]?.given}</span>
              <span style={pill('correct')}>Correct: {answers[reviewIdx]?.a}</span>
              <span style={pill(answers[reviewIdx]?.delta === 0 ? 'ok' : answers[reviewIdx]?.delta === 1 ? 'partial' : 'wrong')}>
                {answers[reviewIdx]?.delta === 0 ? 'Exact match' : answers[reviewIdx]?.delta === 1 ? 'One band off · partial' : 'Off by ' + answers[reviewIdx]?.delta + ' bands'}
              </span>
            </div>
            <div style={{ display: 'flex', justifyContent: 'space-between' }}>
              <button onClick={() => setReviewIdx(Math.max(0, reviewIdx - 1))} disabled={reviewIdx === 0} style={navBtn(reviewIdx === 0)}>← Prev</button>
              <button onClick={() => setReviewIdx(Math.min(answers.length - 1, reviewIdx + 1))} disabled={reviewIdx >= answers.length - 1} style={navBtn(reviewIdx >= answers.length - 1)}>Next →</button>
            </div>
          </div>

          <DrillResultBar slug="decision-profiler" score={overallPct} maxScore={100} accuracyPct={overallPct} metadata={{ length }} />
          {overallPct < 70 && (
            <TutorNudge
              tutor={tutorFor('sjt-profiler')}
              reason={`${overallPct}% is below the band most schools accept. Aisha specialises in SJT pattern-recognition — a 90-min session shifts the curve.`}
              kicker="Your SJT profile has a clear weak axis"
            />
          )}

          <div style={{ marginTop: 20, display: 'flex', gap: 10, flexWrap: 'wrap', marginBottom: 22 }}>
            <button onClick={start} className="btn btn-primary">Run again <Sparkles width="14" height="14" /></button>
            <Link to="/practice" className="btn btn-secondary">Back to Practice</Link>
          </div>
        </main>
      )}
    </div>
  );
}

function pill(kind) {
  const map = {
    your: { bg: 'var(--accent-soft)', fg: 'var(--brand-deep)' },
    correct: { bg: '#e7f7ef', fg: 'var(--live-green)' },
    ok: { bg: '#e7f7ef', fg: 'var(--live-green)' },
    partial: { bg: '#fef3c7', fg: '#b45309' },
    wrong: { bg: '#fee2e2', fg: '#b91c1c' }
  };
  const c = map[kind] || map.your;
  return { padding: '4px 10px', borderRadius: 99, background: c.bg, color: c.fg, fontWeight: 700 };
}
function navBtn(disabled) {
  return { padding: '6px 12px', borderRadius: 8, border: '1px solid var(--border)', background: '#fff', cursor: disabled ? 'not-allowed' : 'pointer', fontSize: 13, fontWeight: 600, color: disabled ? 'var(--text-muted)' : 'var(--text-2)', opacity: disabled ? 0.5 : 1 };
}
